import dayjs from "src/dayjsSetup";

import { getDateFormats, type SupportedLocale } from "./dateFormats";

export type LocaleDateFormatKey =
  | "readable"
  | "short"
  | "readableDateTime"
  | "shortDateTime";

/**
 * Format a date/time value using workspace locale date tokens.
 */
export const formatLocaleDate = (
  value: string | Date | number | null | undefined,
  locale?: SupportedLocale | string | null,
  formatKey: LocaleDateFormatKey = "readable"
): string | null => {
  if (value == null || value === "") {
    return null;
  }

  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format(getDateFormats(locale)[formatKey]);
};

/** Like formatLocaleDate but returns "N/A" when the value is missing or invalid. */
export const formatLocaleDateOrNa = (
  value: string | Date | number | null | undefined,
  locale?: SupportedLocale | string | null,
  formatKey: LocaleDateFormatKey = "readable"
): string => formatLocaleDate(value, locale, formatKey) ?? "N/A";

/**
 * Short display timezone of an instant in the viewer's local zone
 * (e.g. AEST, AEDT, GMT+10). Empty when the value is not a date.
 */
export const displayTimeZoneName = (
  value: string | Date | number | null | undefined,
  locale?: SupportedLocale | string | null
): string => {
  if (value == null || value === "") {
    return "";
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return "";
  }
  const parts = new Intl.DateTimeFormat(locale || undefined, {
    timeZoneName: "short",
  }).formatToParts(parsed.toDate());
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
};

/**
 * Instant in workspace readableDateTime plus the display zone abbreviation.
 */
export const formatLocaleDateTimeWithZone = (
  value: string | Date | number | null | undefined,
  locale?: SupportedLocale | string | null
): string | null => {
  const formatted = formatLocaleDate(value, locale, "readableDateTime");
  if (!formatted) {
    return null;
  }
  const zone = displayTimeZoneName(value, locale);
  return zone ? `${formatted} ${zone}` : formatted;
};

export const formatLocaleDateTimeWithZoneOrNa = (
  value: string | Date | number | null | undefined,
  locale?: SupportedLocale | string | null
): string => formatLocaleDateTimeWithZone(value, locale) ?? "N/A";
